import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Trophy,
  Heart,
  MessageCircle,
  MapPin,
  DollarSign,
  Star,
  Lock,
  TrendingUp,
  Users,
  Calendar,
  Sparkles,
  Gift,
  Loader2,
  CheckCircle2,
  Coins,
  Flame
} from 'lucide-react';
import api from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageLayout } from '@/layouts';
import { VStack, HStack, Grid } from '@/components/core';

interface Achievement {
  id: number;
  name: string;
  description: string;
  category: string;
  points: number;
  coin_reward: number;
  icon: string;
  animation_type: string;
  requirement_type: string;
  requirement_value: number;
}

interface UserAchievementProgress {
  id: number;
  user_id: number;
  achievement_id: number;
  current_progress: number;
  is_completed: boolean;
  completed_at?: string;
  reward_claimed: boolean;
  claimed_at?: string;
  progress_percentage: number;
  is_unlocked: boolean;
  achievement?: Achievement;
}

const categoryColors = {
  social: 'bg-[rgba(var(--color-info-rgb),0.15)] text-[var(--color-info)] border-[rgba(var(--color-info-rgb),0.3)]',
  daily: 'bg-[rgba(var(--color-success-rgb),0.15)] text-[var(--color-success)] border-[rgba(var(--color-success-rgb),0.3)]',
  romantic: 'bg-brand-primary/20 text-brand-primary-dark border-brand-primary/30',
  explorer: 'bg-brand-secondary/20 text-brand-secondary border-brand-secondary/30',
  spender: 'bg-[rgba(var(--color-warning-rgb),0.15)] text-[var(--color-warning)] border-[rgba(var(--color-warning-rgb),0.3)]',
  special: 'bg-[rgba(var(--color-accent-rgb),0.15)] text-[var(--color-accent)] border-[rgba(var(--color-accent-rgb),0.3)]'
};

const iconMap: Record<string, React.ElementType> = {
  Trophy,
  Heart,
  MessageCircle,
  MapPin,
  DollarSign,
  Star,
  Lock,
  TrendingUp,
  Users,
  Calendar,
  Sparkles,
  Gift
};

function AchievementDashboard() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [claimingId, setClaimingId] = useState<number | null>(null);
  const { t } = useTranslation('common');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch user's progress (now returns ALL achievements with user progress embedded)
  const { data: userProgress, isLoading: progressLoading } = useQuery<UserAchievementProgress[]>({
    queryKey: ['achievement-progress'],
    queryFn: async () => {
      const response = await api.get('/achievements/my-progress');
      return response.data;
    }
  });

  // Extract achievements from user progress (they're now embedded)
  const achievements = userProgress?.map(up => up.achievement).filter(Boolean) as Achievement[] | undefined;
  const achievementsLoading = false; // No separate loading since achievements come from userProgress

  // Claim reward mutation
  const claimMutation = useMutation({
    mutationFn: async (achievementId: number) => {
      const response = await api.post('/achievements/claim-reward', {
        reward_type: 'achievement',
        reward_id: achievementId
      });
      return response.data;
    },
    onSuccess: (data) => {
      toast({
        title: t('achievements.rewardClaimed', '🎉 Belohnung erhalten!'),
        description: t('achievements.coinsEarned', '+{{coins}} Coins', { coins: data.coins_earned }),
      });
      void queryClient.invalidateQueries({ queryKey: ['achievement-progress'] });
      void queryClient.invalidateQueries({ queryKey: ['user-balance'] });
      setClaimingId(null);
    },
    onError: () => {
      toast({
        title: t('achievements.claimError', 'Fehler'),
        description: t('achievements.claimErrorDescription', 'Belohnung konnte nicht abgeholt werden'),
        variant: 'destructive'
      });
      setClaimingId(null);
    }
  });

  const handleClaim = (achievementId: number) => {
    setClaimingId(achievementId);
    claimMutation.mutate(achievementId);
  };

  const isLoading = achievementsLoading || progressLoading;

  // Calculate total points and level from user progress
  const totalPoints = userProgress?.reduce((sum, ua) => 
    ua.is_completed && ua.achievement ? sum + ua.achievement.points : sum, 0) ?? 0;
  const level = Math.floor(totalPoints / 100) + 1;
  
  // Count claimable achievements
  const claimableCount = userProgress?.filter(ua => ua.is_completed && !ua.reward_claimed).length ?? 0;

  const getProgressForAchievement = (achievementId: number): UserAchievementProgress | undefined => {
    return userProgress?.find(p => p.achievement_id === achievementId || p.achievement?.id === achievementId);
  };

  // Filter user progress directly (since achievements are embedded)
  const filteredProgress = userProgress?.filter(up => 
    !selectedCategory || up.achievement?.category === selectedCategory
  );

  const filteredAchievements = filteredProgress?.map(up => up.achievement).filter(Boolean) as Achievement[] | undefined;

  const categories = [
    { id: 'social', name: 'Social', icon: Users },
    { id: 'daily', name: 'Täglich', icon: Calendar },
    { id: 'romantic', name: 'Romantisch', icon: Heart },
    { id: 'explorer', name: 'Entdecker', icon: MapPin },
    { id: 'spender', name: 'Ausgaben', icon: DollarSign },
    { id: 'special', name: 'Spezial', icon: Star }
  ];
  
  useEffect(() => {
    const main = document.querySelector('main.app-main');
    if (!main) return;
    const classes = ['design-page-gradient', 'page-achievements'];
    main.classList.add(...classes);
    return () => {
      main.classList.remove(...classes);
    };
  }, []);

  if (isLoading) {
    return (
      <PageLayout maxWidth="xl" padding="md" className="relative">
        {/* Hero Section Skeleton */}
        <section className="relative">
          {/* Background Effects */}
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-white/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-white/15 rounded-full blur-3xl" />

          <div className="relative pb-token-3xl md:pb-token-3xl">
            <VStack gap="lg" className="text-center">
              {/* Hero Title Skeleton */}
              <VStack gap="sm">
                <Skeleton className="h-12 md:h-14 w-64 mx-auto bg-white/10" />
                <Skeleton className="h-12 md:h-14 w-48 mx-auto bg-white/10" />
              </VStack>

              {/* Hero Subtitle Skeleton */}
              <VStack gap="sm" className="max-w-3xl mx-auto">
                <Skeleton className="h-5 w-full bg-white/10" />
                <Skeleton className="h-5 w-3/4 mx-auto bg-white/10" />
              </VStack>

              {/* Level & Points Cards Skeleton */}
              <HStack wrap gap="md" justify="center" className="flex-col sm:flex-row pt-token-md">
                <div className="design-glass px-token-xl py-token-lg rounded-token-card">
                  <HStack align="center" gap="sm">
                    <Skeleton className="w-12 h-12 rounded-full bg-white/10" />
                    <VStack gap="sm" className="text-left">
                      <Skeleton className="h-4 w-12 bg-white/10" />
                      <Skeleton className="h-8 w-8 bg-white/10" />
                    </VStack>
                  </HStack>
                </div>
                <div className="design-glass px-token-xl py-token-lg rounded-token-card">
                  <HStack align="center" gap="sm">
                    <Skeleton className="w-12 h-12 rounded-full bg-white/10" />
                    <VStack gap="sm" className="text-left">
                      <Skeleton className="h-4 w-16 bg-white/10" />
                      <Skeleton className="h-8 w-12 bg-white/10" />
                    </VStack>
                  </HStack>
                </div>
              </HStack>
            </VStack>
          </div>
        </section>

        {/* Filter Section Skeleton */}
        <section className="relative pb-token-3xl">
          <div className="max-w-7xl mx-auto px-token-lg">
            <div className="text-center mb-token-xs2">
              <Skeleton className="h-9 md:h-10 w-72 mx-auto mb-token-md bg-white/10" />
              <Skeleton className="h-5 w-96 max-w-full mx-auto bg-white/10" />
            </div>

            <HStack wrap gap="sm" justify="center" align="center">
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <Skeleton key={i} className="h-12 w-24 rounded-token-card bg-white/10" />
              ))}
            </HStack>
          </div>
        </section>

        {/* Achievement Grid Skeleton */}
        <section className="relative pb-token-3xl">
          <div className="max-w-7xl mx-auto px-token-lg">
            <Grid cols={1} colsMd={2} colsLg={3} gap="lg">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="design-card border-white/10">
                  <div className="p-token-lg">
                    {/* Icon and Status Badge Skeleton */}
                    <HStack align="start" justify="between" className="mb-token-md">
                      <Skeleton className="w-12 h-12 rounded-token-card bg-white/10" />
                      <Skeleton className="w-5 h-5 rounded-full bg-white/10" />
                    </HStack>

                    {/* Name Skeleton */}
                    <Skeleton className="h-6 w-3/4 mb-token-sm bg-white/10" />

                    {/* Description Skeleton */}
                    <VStack gap="xs" className="mb-token-md">
                      <Skeleton className="h-4 w-full bg-white/10" />
                      <Skeleton className="h-4 w-2/3 bg-white/10" />
                    </VStack>

                    {/* Progress Bar Skeleton */}
                    <div className="mb-token-md">
                      <HStack justify="between" className="mb-token-xs">
                        <Skeleton className="h-4 w-16 bg-white/10" />
                        <Skeleton className="h-4 w-12 bg-white/10" />
                      </HStack>
                      <Skeleton className="h-2.5 w-full rounded-full bg-white/10" />
                    </div>

                    {/* Coin Reward Skeleton */}
                    <HStack align="center" justify="between" className="p-token-sm rounded-token-button bg-white/5">
                      <HStack align="center" gap="sm">
                        <Skeleton className="h-5 w-5 rounded bg-white/10" />
                        <Skeleton className="h-4 w-16 bg-white/10" />
                      </HStack>
                      <Skeleton className="h-6 w-10 bg-white/10" />
                    </HStack>
                  </div>
                </div>
              ))}
            </Grid>
          </div>
        </section>
      </PageLayout>
    );
  }

  return (
    <PageLayout maxWidth="xl" padding="md" className="relative">
      {/* Hero Section */}
      <section className="relative">
        {/* Background Effects */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-white/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-white/15 rounded-full blur-3xl" />
        
        <div className="relative fade-in-up pb-token-3xl md:pb-token-3xl">
          <VStack gap="lg" className="text-center">
            {/* Hero Title */}
            <h1 className="text-5xl md:text-6xl font-bold design-text-primary leading-normal">
              {t('achievements.title')} <br />
              <span className="gradient-text pb-token-sm">{t('achievements.titleHighlight')}</span>
            </h1>

            {/* Hero Subtitle */}
            <p className="text-xl design-text-secondary max-w-3xl mx-auto leading-relaxed">
              {t('achievements.subtitle')}
            </p>

            {/* Level & Points Cards */}
            <HStack wrap gap="md" justify="center" className="flex-col sm:flex-row pt-token-md">
              <div className="design-glass px-token-xl py-token-lg rounded-token-card">
                <HStack align="center" gap="sm">
                  <div className="w-12 h-12 gradient-primary-smooth rounded-full flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm design-text-secondary">{t('achievements.level')}</p>
                    <p className="text-3xl font-bold design-text-primary">{level}</p>
                  </div>
                </HStack>
              </div>
              <div className="design-glass px-token-xl py-token-lg rounded-token-card">
                <HStack align="center" gap="sm">
                  <div className="w-12 h-12 gradient-secondary-smooth rounded-full flex items-center justify-center">
                    <Trophy className="h-6 w-6 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm design-text-secondary">{t('achievements.points')}</p>
                    <p className="text-3xl font-bold design-text-primary">{totalPoints}</p>
                  </div>
                </HStack>
              </div>
            </HStack>
            
            {/* Claimable Achievements Banner */}
            {claimableCount > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mt-token-lg px-token-lg py-token-md bg-gradient-to-r from-[rgba(var(--color-warning-rgb),0.2)] via-[rgba(var(--color-warning-rgb),0.15)] to-[rgba(var(--color-warning-rgb),0.2)] border border-[rgba(var(--color-warning-rgb),0.3)] rounded-token-card"
              >
                <HStack align="center" justify="center" gap="sm">
                  <Gift className="h-6 w-6 text-[var(--color-warning)] animate-bounce" />
                  <span className="text-[var(--color-warning)] font-semibold">
                    {claimableCount === 1
                      ? t('achievements.oneRewardReady', '1 Belohnung wartet auf dich!')
                      : t('achievements.rewardsReady', '{{count}} Belohnungen warten auf dich!', { count: claimableCount })
                    }
                  </span>
                  <Sparkles className="h-5 w-5 text-[var(--color-warning)] animate-pulse" />
                </HStack>
              </motion.div>
            )}
          </VStack>
        </div>
      </section>

      {/* Filter Section */}
      <section className="relative pb-token-3xl">
        <div className="text-center mb-token-xs2 fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold design-text-primary mb-token-md">
              {t('achievements.filterTitle')} <span className="gradient-text">{t('achievements.filterTitleHighlight')}</span>
            </h2>
            <p className="design-text-secondary max-w-2xl mx-auto leading-relaxed">
              {t('achievements.filterSubtitle')}
            </p>
          </div>
          
          <HStack justify="center">
            <Tabs
              value={selectedCategory || 'all'}
              onValueChange={(value) => setSelectedCategory(value === 'all' ? null : value)}
            >
              <TabsList className="inline-flex flex-wrap h-auto gap-token-sm p-token-sm">
              <TabsTrigger value="all" className="px-token-md py-token-sm">
                {t('achievements.showAll')}
              </TabsTrigger>
              {categories.map(category => {
                const Icon = category.icon;
                return (
                  <TabsTrigger
                    key={category.id}
                    value={category.id}
                    className="px-token-md py-token-sm flex items-center gap-token-sm"
                  >
                    <Icon className="h-4 w-4" />
                    <span>{t(`achievements.categories.${category.id}`)}</span>
                  </TabsTrigger>
                );
              })}
              </TabsList>
            </Tabs>
          </HStack>
      </section>

      {/* Achievement Grid */}
      <section className="relative pb-token-3xl">
        <Grid cols={1} colsMd={2} colsLg={3} gap="lg">
            <AnimatePresence>
            {filteredProgress?.map(progressData => {
              const achievement = progressData.achievement;
              if (!achievement) return null;
              
              const isCompleted = progressData.is_completed ?? false;
              const isRewardClaimed = progressData.reward_claimed ?? false;
              const currentProgress = progressData.current_progress ?? 0;
              const targetProgress = achievement.requirement_value || 1;
              const progressPercentage = Math.min((currentProgress / targetProgress) * 100, 100);
              const Icon = iconMap[achievement.icon] || Trophy;
              
              // Three states: locked (0), in_progress (>0 but not complete), completed
              const isLocked = currentProgress === 0;
              const isInProgress = currentProgress > 0 && !isCompleted;
              const canClaim = isCompleted && !isRewardClaimed;
              const isClaiming = claimingId === achievement.id;

              return (
                <motion.div
                  key={achievement.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className={`relative overflow-hidden design-card transition-all transform hover:scale-[1.02] ${
                    canClaim
                      ? 'border-[rgba(var(--color-warning-rgb),0.5)] shadow-token-card shadow-[rgba(var(--color-warning-rgb),0.2)] ring-2 ring-[rgba(var(--color-warning-rgb),0.3)]'
                      : isCompleted
                        ? 'border-[rgba(var(--color-success-rgb),0.3)] shadow-token-card shadow-[rgba(var(--color-success-rgb),0.2)]'
                        : isInProgress
                          ? 'border-brand-secondary/30 shadow-token-dropdown shadow-brand-secondary/10'
                          : 'border-white/10 hover:border-white/20'
                  }`}
                >
                  {/* In Progress Glow Effect */}
                  {isInProgress && (
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute inset-0 bg-gradient-to-br from-brand-secondary/10 via-transparent to-brand-primary/10 animate-pulse" />
                      <Flame className="absolute top-3 right-3 h-5 w-5 text-[var(--color-warning)] animate-bounce" />
                    </div>
                  )}

                  {/* Claimable Glow Effect */}
                  {canClaim && (
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute inset-0 bg-gradient-to-br from-[rgba(var(--color-warning-rgb),0.2)] via-transparent to-[rgba(var(--color-warning-rgb),0.15)] animate-pulse" />
                      <Sparkles className="absolute top-2 right-2 h-5 w-5 text-[var(--color-warning)] animate-spin" style={{ animationDuration: '3s' }} />
                      <Sparkles className="absolute bottom-2 left-2 h-4 w-4 text-[var(--color-warning)] animate-ping" style={{ animationDuration: '2s' }} />
                    </div>
                  )}

                  {/* Completed Animation */}
                  {isCompleted && isRewardClaimed && (
                    <div className="absolute inset-0 pointer-events-none">
                      <Sparkles className="absolute top-2 right-2 h-4 w-4 text-[var(--color-success)] animate-pulse" />
                    </div>
                  )}

                  <div className={`p-token-lg ${isLocked ? 'opacity-50' : ''}`}>
                    {/* Status Badge */}
                    <HStack align="start" justify="between" className="mb-token-md">
                      <div className={`w-12 h-12 rounded-token-card flex items-center justify-center transition-all ${
                        canClaim
                          ? 'bg-gradient-to-br from-[var(--color-warning)] to-[var(--color-warning-dark,var(--color-warning))] animate-pulse'
                          : isCompleted
                            ? 'gradient-primary-smooth'
                            : isInProgress
                              ? 'bg-gradient-to-br from-brand-secondary/80 to-brand-primary/80'
                              : 'bg-white/10'
                      }`}>
                        <Icon className={`h-6 w-6 ${
                          canClaim || isCompleted || isInProgress ? 'text-white' : 'text-muted-foreground'
                        }`} />
                      </div>
                      
                      {/* Status Indicator */}
                      {canClaim ? (
                        <span className="px-token-sm py-token-xs text-xs font-bold bg-[rgba(var(--color-warning-rgb),0.2)] text-[var(--color-warning)] rounded-full animate-pulse">
                          {t('achievements.claimable', 'Abholen!')}
                        </span>
                      ) : isCompleted ? (
                        <CheckCircle2 className="h-5 w-5 text-[var(--color-success)]" />
                      ) : isInProgress ? (
                        <span className="px-token-sm py-token-xs text-xs font-medium bg-brand-secondary/20 text-brand-secondary-light rounded-full flex items-center gap-token-xs">
                          <Flame className="h-3 w-3" />
                          {Math.round(progressPercentage)}%
                        </span>
                      ) : (
                        <Lock className="h-5 w-5 text-muted-foreground" />
                      )}
                    </HStack>

                    {/* Name und Beschreibung */}
                    <h3 className={`text-lg font-semibold mb-token-sm ${
                      canClaim
                        ? 'text-[var(--color-warning)]'
                        : isCompleted || isInProgress
                          ? 'design-text-primary'
                          : 'design-text-muted'
                    }`}>
                      {achievement.name}
                    </h3>
                    <p className={`text-sm mb-token-md ${
                      isCompleted || isInProgress ? 'design-text-secondary' : 'design-text-muted'
                    }`}>
                      {achievement.description}
                    </p>

                    {/* Progress Bar */}
                    <div className="mb-token-md">
                      <HStack justify="between" className="text-sm mb-token-xs">
                        <span className={isCompleted || isInProgress ? 'design-text-secondary' : 'design-text-muted'}>
                          {t('achievements.progress')}
                        </span>
                        <span className={`font-medium ${
                          canClaim
                            ? 'text-[var(--color-warning)]'
                            : isCompleted
                              ? 'text-[var(--color-success)]'
                              : isInProgress
                                ? 'text-brand-secondary-light'
                                : 'design-text-muted'
                        }`}>
                          {currentProgress} / {targetProgress}
                        </span>
                      </HStack>
                      <div className="w-full bg-white/10 rounded-full h-2.5 overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${progressPercentage}%` }}
                          transition={{ duration: 0.8, ease: 'easeOut' }}
                          className={`h-2.5 rounded-full ${
                            canClaim
                              ? 'bg-gradient-to-r from-[var(--color-warning)] to-[var(--color-warning)]'
                              : isCompleted
                                ? 'bg-gradient-to-r from-[var(--color-success)] to-[var(--color-success)]'
                                : isInProgress
                                  ? 'bg-gradient-to-r from-brand-secondary to-brand-primary'
                                  : 'bg-white/20'
                          }`}
                        />
                      </div>
                    </div>

                    {/* Coin Belohnung */}
                    <HStack align="center" justify="between" className={`p-token-sm rounded-token-button ${
                      canClaim
                        ? 'bg-[rgba(var(--color-warning-rgb),0.1)] border border-[rgba(var(--color-warning-rgb),0.3)]'
                        : 'bg-white/5'
                    }`}>
                      <HStack align="center" gap="sm">
                        <Coins className={`h-5 w-5 ${canClaim ? 'text-[var(--color-warning)]' : 'design-text-muted'}`} />
                        <span className={`text-sm font-medium ${canClaim ? 'text-[var(--color-warning)]' : 'design-text-secondary'}`}>
                          {t('achievements.reward')}
                        </span>
                      </HStack>
                      <span className={`font-bold text-lg ${
                        canClaim ? 'text-[var(--color-warning)]' : isCompleted ? 'text-[var(--color-success)]' : 'design-text-muted'
                      }`}>
                        +{achievement.coin_reward}
                      </span>
                    </HStack>

                    {/* Claim Button for completed but unclaimed */}
                    {canClaim && (
                      <Button
                        onClick={() => handleClaim(achievement.id)}
                        disabled={isClaiming}
                        className="w-full mt-token-md bg-gradient-to-r from-[var(--color-warning)] to-[var(--color-warning)] hover:opacity-90 text-white font-bold py-token-sm rounded-token-card transition-all transform hover:scale-[1.02] active:scale-[0.98]"
                      >
                        {isClaiming ? (
                          <>
                            <Loader2 className="h-5 w-5 mr-token-sm animate-spin" />
                            {t('achievements.claiming', 'Wird abgeholt...')}
                          </>
                        ) : (
                          <>
                            <Gift className="h-5 w-5 mr-token-sm" />
                            {t('achievements.claimReward', 'Belohnung abholen')}
                          </>
                        )}
                      </Button>
                    )}

                    {/* Claimed Date */}
                    {isCompleted && isRewardClaimed && progressData?.claimed_at && (
                      <div className="mt-token-sm pt-token-sm border-t border-white/10">
                        <p className="text-xs text-[var(--color-success)] font-medium flex items-center">
                          <CheckCircle2 className="h-3 w-3 mr-token-xs" />
                          {t('achievements.claimedOn', 'Abgeholt am')} {new Date(progressData.claimed_at).toLocaleDateString('de-DE')}
                        </p>
                      </div>
                    )}

                    {/* Completed but not claimed yet date */}
                    {isCompleted && !isRewardClaimed && progressData?.completed_at && (
                      <div className="mt-token-sm pt-token-sm border-t border-[rgba(var(--color-warning-rgb),0.2)]">
                        <p className="text-xs text-[var(--color-warning)] font-medium flex items-center">
                          <Sparkles className="h-3 w-3 mr-token-xs" />
                          {t('achievements.unlockedOn')} {new Date(progressData.completed_at).toLocaleDateString('de-DE')}
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
            {/* Empty State */}
            {filteredAchievements?.length === 0 && (
              <div className="design-card p-token-2xl text-center fade-in-up">
                <div className="w-20 h-20 gradient-primary-smooth rounded-full flex items-center justify-center mx-auto mb-token-md">
                  <Trophy className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-xl font-bold design-text-primary mb-token-sm">
                  {t('achievements.noAchievements')}
                </h3>
                <p className="design-text-secondary">
                  {t('achievements.noAchievementsDescription')}
                </p>
              </div>
            )}
        </Grid>
      </section>
    </PageLayout>
  );
}

export default AchievementDashboard;